import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
  { label: "About", id: "about" },
  { label: "Expertise", id: "services" },
  { label: "Cases", id: "cases" },
  { label: "Aftercare", id: "aftercare" },
  { label: "Portfolio", id: "portfolio" },
  { label: "Contact", id: "contact" },
];

const scrollTo = (id) => scrollToId(id);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.nav
      data-testid="main-navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 1.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "backdrop-blur-xl bg-ivory/80 border-b border-champagne/20" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        <button
          data-testid="nav-logo"
          onClick={() => scrollTo("hero")}
          className="text-left leading-tight"
        >
          <span className="block font-display text-2xl md:text-[28px] leading-none text-[#1a365d] tracking-[-0.01em] font-semibold">
            Dr. Priya H. Mishra
          </span>
          <span className="block mt-1.5 text-[10px] md:text-[12px] uppercase tracking-[0.32em] text-[#1a365d]/70">
            Prosthodontist &amp; Implantologist
          </span>
        </button>

        <div className="hidden md:flex items-center gap-10">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => scrollTo(l.id)}
              className="text-sm uppercase tracking-[0.15em] text-emerald/70 hover:text-emerald transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden text-emerald"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="md:hidden overflow-hidden bg-ivory border-t border-champagne/20"
          >
            <div className="flex flex-col px-6 py-6 gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => {
                    scrollTo(l.id);
                    setOpen(false);
                  }}
                  className="text-left text-base uppercase tracking-[0.15em] text-emerald/80"
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
